import { useTranslation } from 'react-i18next';
import { format as dateFnsFormat, parseISO } from 'date-fns';
import { enUS, zhCN, zhTW } from 'date-fns/locale';
import type { Locale } from 'date-fns';

// Map i18n language codes to date-fns locales
const DATE_LOCALES: Record<string, Locale> = {
  'en': enUS,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
};

export const useFormatting = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const dateLocale = DATE_LOCALES[currentLanguage] || enUS;

  // Date formatting
  const formatDate = (dateStr: string, formatStr: string): string => {
    try {
      return dateFnsFormat(parseISO(dateStr), formatStr, { locale: dateLocale });
    } catch {
      return dateStr;
    }
  };

  // Number formatting with locale
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
    try {
      return new Intl.NumberFormat(currentLanguage, options).format(value);
    } catch {
      return value.toString();
    }
  };

  // Currency formatting
  const formatCurrency = (value: number, compact: boolean = false): string => {
    if (compact) {
      // Abbreviated format: $10K, $1.2M, etc.
      if (value >= 1000000) {
        const millions = value / 1000000;
        return `$${formatNumber(millions, { maximumFractionDigits: 1 })}M`;
      } else if (value >= 1000) {
        const thousands = value / 1000;
        return `$${formatNumber(thousands, { maximumFractionDigits: 0 })}K`;
      }
      return `$${formatNumber(value, { maximumFractionDigits: 0 })}`;
    }

    // Full format with locale-aware thousand separators
    return formatNumber(value, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Percentage formatting
  const formatPercentage = (value: number, decimals: number = 1): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${formatNumber(value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}%`;
  };

  return {
    formatDate,
    formatNumber,
    formatCurrency,
    formatPercentage,
    currentLanguage,
    dateLocale,
  };
};
